/**
 * Tiny i18n layer. English is the source dictionary (its keys are the type);
 * every other locale must provide every key, which the compiler enforces.
 * Messages may interpolate `{name}` placeholders, and take one/other plural
 * forms keyed on the `n` variable.
 */
import { computed } from "@preact/signals";
import { prefs } from "./prefs";

type Msg = string | { one: string; other: string };

const en = {
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.copy": "Copy",
  "common.deleteForever": "Delete forever",
  "common.dismiss": "Dismiss",
  "common.emptyNow": "Empty now",
  "common.loading": "Loading",
  "common.more": "More",
  "common.no": "no",
  "common.yes": "yes",
  "common.noSubject": "(no subject)",
  "common.remove": "Remove",
  "common.rename": "Rename",
  "common.retry": "Try again",
  "common.saveChanges": "Save changes",
  "common.saving": "Saving…",
  "common.undo": "Undo",

  "folder.inbox": "Inbox",
  "folder.drafts": "Drafts",
  "folder.sent": "Sent",
  "folder.archive": "Archive",
  "folder.junk": "Spam",
  "folder.trash": "Trash",
  "folder.flagged": "Starred",
  "folder.all": "All mail",

  "date.today": "Today",
  "date.yesterday": "Yesterday",
  "date.week": "Earlier this week",
  "date.month": "Earlier this month",

  "nav.label": "Folders and account",
  "nav.collapse": "Collapse sidebar",
  "nav.expand": "Expand sidebar",
  "nav.open": "Open folders",
  "nav.folders": "Folders",
  "nav.newFolder": "New folder",
  "nav.newSubfolder": "New subfolder",
  "nav.folderName": "Folder name",
  "nav.subfolderOf": "New folder inside {name}",
  "nav.noFolders": "No folders yet",
  "nav.folderOptions": "Folder options",
  "nav.markAllRead": "Mark all as read",
  "nav.emptyFolder": "Empty folder",
  "nav.deleteFolder": "Delete folder",
  "nav.collapseFolder": "Hide subfolders",
  "nav.expandFolder": "Show subfolders",
  "nav.signOut": "Sign out",

  "list.label": "Messages",
  "list.summary": { one: "1 message", other: "{n} messages" },
  "list.summaryUnread": "{n} messages, {u} unread",
  "list.summaryFilterUnread": { one: "1 unread message", other: "{n} unread messages" },
  "list.summaryFilterStarred": { one: "1 starred message", other: "{n} starred messages" },
  "list.refresh": "Check for new mail",
  "list.folderActions": "Folder actions",
  "list.filter": "Show",
  "list.filter.all": "All",
  "list.filter.unread": "Unread",
  "list.filter.starred": "Starred",
  "list.selectAll": "Select all",
  "list.clearSelection": "Clear selection",
  "list.select": "Select message",
  "list.deselect": "Deselect message",
  "list.selected": { one: "1 selected", other: "{n} selected" },
  "list.loadError": "Couldn't load messages",
  "list.loadMore": "Load more",
  "list.toPrefix": "To:",
  "list.noRecipients": "(no recipients)",
  "list.draftTag": "Draft",
  "list.unread": "unread",

  "search.label": "Search mail",
  "search.placeholder": "Search in {folder}",
  "search.options": "Search options",
  "search.clear": "Clear search",
  "search.results": "Search results",
  "search.searching": "Searching…",
  "search.count": { one: "1 result", other: "{n} results" },
  "search.lookIn": "Match",
  "search.field.all": "Everything",
  "search.field.from": "From",
  "search.field.to": "To",
  "search.field.subject": "Subject",
  "search.field.body": "Body",
  "search.since": "From date",
  "search.before": "Until date",
  "search.onlyUnread": "Only unread",
  "search.onlyStarred": "Only starred",
  "search.allFolders": "Search all folders",
  "search.reset": "Reset",
  "search.apply": "Search",
  "search.noneTitle": "Nothing matches",
  "search.noneBody": "Try other words, or search all folders from the options.",

  "empty.inboxTitle": "You're all caught up",
  "empty.inboxBody": "New mail will show up here.",
  "empty.draftsTitle": "No drafts",
  "empty.draftsBody": "Messages you start and don't send are saved here automatically.",
  "empty.sentTitle": "Nothing sent yet",
  "empty.sentBody": "Messages you send will appear here.",
  "empty.trashTitle": "Trash is empty",
  "empty.trashBody": "Deleted messages stay here until you empty it.",
  "empty.spamTitle": "No spam",
  "empty.spamBody": "Messages marked as spam end up here.",
  "empty.folderTitle": "This folder is empty",
  "empty.folderBody": "Drag messages onto it in the sidebar to file them here.",
  "empty.unreadTitle": "No unread messages",
  "empty.unreadBody": "Everything in this folder has been read.",
  "empty.starredTitle": "No starred messages",
  "empty.starredBody": "Star a message to find it here quickly.",
  "empty.showAll": "Show all messages",

  "action.archive": "Archive",
  "action.delete": "Delete",
  "action.spam": "Mark as spam",
  "action.notSpam": "Not spam",
  "action.move": "Move to…",
  "action.markRead": "Mark as read",
  "action.markUnread": "Mark as unread",
  "action.star": "Star",
  "action.unstar": "Remove star",
  "action.reply": "Reply",
  "action.replyAll": "Reply all",
  "action.forward": "Forward",

  "reader.label": "Message",
  "reader.back": "Back to list",
  "reader.position": "{i} of {n}",
  "reader.newer": "Newer message",
  "reader.older": "Older message",
  "reader.print": "Print",
  "reader.source": "View original",
  "reader.downloadEml": "Download as .eml",
  "reader.copySender": "Copy sender address",
  "reader.loadError": "Couldn't open this message",
  "reader.remoteBlocked": "Images from the sender are hidden so they can't track when you read this.",
  "reader.showImages": "Show images",
  "reader.alwaysShow": "Always show",
  "reader.mailingList": "This message came from a mailing list.",
  "reader.unsubscribe": "Unsubscribe",
  "reader.toLabel": "To",
  "reader.me": "me",
  "reader.undisclosed": "undisclosed recipients",
  "reader.andMore": "{list} and {n} more",
  "reader.replyTo": "Reply to",
  "reader.thread": { one: "1 message in this conversation", other: "{n} messages in this conversation" },
  "reader.empty": "This message has no text.",
  "reader.body": "Message body",
  "reader.attachments": "Attachments",
  "reader.attachmentsCount": { one: "1 attachment", other: "{n} attachments" },
  "reader.view": "Preview",
  "reader.download": "Download",
  "reader.openTab": "Open in a new tab",
  "reader.emptyTitle": "Pick a message to read",
  "reader.emptyBody": "It opens here, next to your list.",
  "reader.hintNavigate": "move",
  "reader.hintCompose": "write",
  "reader.hintHelp": "all shortcuts",

  "eml.title": "Attached message",
  "eml.sub": "Read-only preview of a message attached to this email",
  "eml.loading": "Opening the attached message…",
  "eml.error": "Could not preview this attachment.",
  "eml.nested": { one: "1 attachment inside", other: "{n} attachments inside" },

  "compose.new": "New message",
  "compose.reply": "Reply",
  "compose.replyAll": "Reply all",
  "compose.forward": "Forward",
  "compose.draft": "Draft",
  "compose.from": "From",
  "compose.to": "To",
  "compose.date": "Date",
  "compose.subject": "Subject",
  "compose.subjectPlaceholder": "Subject",
  "compose.body": "Message",
  "compose.bodyPlaceholder": "Write your message…",
  "compose.recipientPlaceholder": "Name or email",
  "compose.invalidAddress": "This doesn't look like an email address",
  "compose.removeRecipient": "Remove {name}",
  "compose.removeAttachment": "Remove {name}",
  "compose.send": "Send",
  "compose.sending": "Sending…",
  "compose.sendAnyway": "Send anyway",
  "compose.attach": "Attach files",
  "compose.dropFiles": "Drop files to attach them",
  "compose.discard": "Discard",
  "compose.saveClose": "Save draft",
  "compose.saving": "Saving…",
  "compose.saved": "Draft saved",
  "compose.minimize": "Minimize",
  "compose.restore": "Restore",
  "compose.maximize": "Full screen",
  "compose.dock": "Exit full screen",
  "compose.unsavedTitle": "Keep this draft?",
  "compose.unsavedBody": "You have changes that haven't been saved yet.",
  "compose.linkPrompt": "Link address (https:// or mailto:)",
  "compose.needRecipient": "Add at least one recipient.",
  "compose.badRecipient": "Check this address: {addr}",
  "compose.stillAttaching": "Wait for the attachments to finish loading.",
  "compose.tooLarge": "{name} is too large: attachments are limited to 25 MB in total.",
  "compose.readFailed": "Couldn't read {name}.",
  "compose.quoteHeader": "On {date}, {who} wrote:",
  "compose.fwdHeader": "---------- Forwarded message ----------",

  "format.bold": "Bold",
  "format.italic": "Italic",
  "format.underline": "Underline",
  "format.bullets": "Bulleted list",
  "format.numbers": "Numbered list",
  "format.quote": "Quote",
  "format.link": "Insert link",
  "format.clear": "Clear formatting",

  "confirm.deleteForever.title": { one: "Delete this message forever?", other: "Delete {n} messages forever?" },
  "confirm.deleteForever.body": "They're already in the trash. This can't be undone.",
  "confirm.empty.title": "Empty {folder}?",
  "confirm.empty.body": "Every message in it will be deleted for good. This can't be undone.",
  "confirm.noSubject.title": "Send without a subject?",
  "confirm.noSubject.body": "A subject helps the recipient know what your message is about.",
  "confirm.deleteFolder.title": "Delete “{name}”?",
  "confirm.deleteFolder.kids": "Its subfolders may be deleted too.",
  "confirm.deleteFolder.notEmpty": { one: "It contains 1 message.", other: "It contains {n} messages." },

  "toast.archived": { one: "Archived", other: "{n} archived" },
  "toast.trashed": { one: "Moved to Trash", other: "{n} moved to Trash" },
  "toast.deleted": { one: "Deleted forever", other: "{n} deleted forever" },
  "toast.spammed": { one: "Marked as spam", other: "{n} marked as spam" },
  "toast.moved": { one: "Moved to {folder}", other: "{n} moved to {folder}" },
  "toast.undone": "Undone",
  "toast.actionFailed": "That didn't work. Check your connection and try again.",
  "toast.noArchive": "This account has no Archive folder.",
  "toast.noSpam": "This account has no Spam folder.",
  "toast.sending": "Sending…",
  "toast.sent": "Message sent",
  "toast.sentNoCopy": "Message sent, but no copy was saved to Sent",
  "toast.draftDiscarded": "Draft discarded",
  "toast.folderCreated": "Folder “{name}” created",
  "toast.markedAllRead": "Everything in {folder} marked as read",
  "toast.emptied": { one: "1 message deleted", other: "{n} messages deleted" },
  "toast.copied": "Copied",
  "toast.copyFailed": "Couldn't copy to the clipboard",

  "notify.one": "New message",
  "notify.many": "{n} new messages",

  "move.title": "Move to folder",
  "move.placeholder": { one: "Move 1 message to…", other: "Move {n} messages to…" },
  "move.create": "Create folder “{name}” and move",
  "move.none": "No matching folders",

  "palette.title": "Command palette",
  "palette.placeholder": "Type a command, folder or search…",
  "palette.go": "Go to",
  "palette.actions": "Actions",
  "palette.view": "View",
  "palette.search": "Search",
  "palette.goTo": "Go to {folder}",
  "palette.markFolderRead": "Mark everything in {folder} as read",
  "palette.lightTheme": "Switch to light theme",
  "palette.darkTheme": "Switch to dark theme",
  "palette.compact": "Use compact rows",
  "palette.comfortable": "Use comfortable rows",
  "palette.paneOff": "Hide the reading pane",
  "palette.paneRight": "Show the reading pane",
  "palette.searchHere": "Search “{q}” in {folder}",
  "palette.searchAll": "Search “{q}” in all folders",

  "shortcuts.title": "Keyboard shortcuts",
  "shortcuts.navigation": "Navigation",
  "shortcuts.actions": "Messages",
  "shortcuts.writing": "Writing",
  "shortcuts.next": "Older message",
  "shortcuts.prev": "Newer message",
  "shortcuts.goInbox": "Go to Inbox",
  "shortcuts.goSent": "Go to Sent",
  "shortcuts.goDrafts": "Go to Drafts",
  "shortcuts.goArchive": "Go to Archive",
  "shortcuts.search": "Search",
  "shortcuts.palette": "Command palette",
  "shortcuts.escape": "Close or go back",
  "shortcuts.toggleRead": "Mark read / unread",
  "shortcuts.toggleStar": "Star / unstar",
  "shortcuts.select": "Select message",
  "shortcuts.undo": "Undo last action",
  "shortcuts.saveDraft": "Save draft",

  "layout.resize": "Resize message list",

  "theme.light": "Light",
  "theme.dark": "Dark",
  "theme.auto": "Auto",

  "settings.title": "Settings",
  "settings.general": "General",
  "settings.account": "Account",
  "settings.privacy": "Privacy",
  "settings.developer": "Developers",
  "settings.admin": "Administration",
  "settings.appearance": "Appearance",
  "settings.language": "Language",
  "settings.theme": "Theme",
  "settings.density": "Message list",
  "settings.densityHelp": "Compact fits more messages on screen.",
  "settings.comfortable": "Comfortable",
  "settings.compact": "Compact",
  "settings.readingPane": "Reading pane",
  "settings.readingPaneHelp": "Read next to the list, or open messages full width.",
  "settings.paneRight": "On the right",
  "settings.paneOff": "Off",
  "settings.previews": "Message previews",
  "settings.previewsHelp": "Show the first line of each message in the list.",
  "settings.behaviour": "Behaviour",
  "settings.afterMove": "After archiving or deleting",
  "settings.afterMoveHelp": "What to show once the open message is gone.",
  "settings.afterNext": "Next message",
  "settings.afterList": "The list",
  "settings.undoSend": "Undo send",
  "settings.undoSendHelp": "How long you have to take back a message after pressing Send.",
  "settings.undoOff": "Off",
  "settings.seconds": "{n} seconds",
  "settings.notifications": "Desktop notifications",
  "settings.notificationsHelp": "Get notified about new mail while this tab is in the background.",
  "settings.notificationsDenied": "Notifications are blocked by your browser for this site.",
  "settings.identity": "Name and signature",
  "settings.identityHelp": "Shown to people you write to from {email}.",
  "settings.displayName": "Your name",
  "settings.fromPreview": "Recipients see",
  "settings.signature": "Signature",
  "settings.signaturePlaceholder": "Added to the end of new messages",
  "settings.signatureOnReplies": "Add signature to replies and forwards",
  "settings.signatureOnRepliesHelp": "Placed above the quoted message.",
  "settings.saved": "Settings saved",

  "password.title": "Password",
  "password.help": "Change the password for your mailbox. It must be {min}–{max} characters long.",
  "password.unavailable": "Changing your password from here isn't available yet. You can update it on the mail server's account page.",
  "password.openAccountPage": "Open account page",
  "password.current": "Current password",
  "password.new": "New password",
  "password.confirm": "Confirm new password",
  "password.change": "Change password",
  "password.changing": "Changing…",
  "password.changed": "Password changed. You stay signed in on this device.",
  "password.fillAll": "Fill in all three fields.",
  "password.length": "The new password must be between {min} and {max} characters.",
  "password.mismatch": "The new password and its confirmation don't match.",
  "password.same": "The new password must be different from the current one.",
  "password.failed": "Couldn't change the password.",

  "privacy.title": "Remote images",
  "privacy.help": "Senders can use remote images to see when and where you open their mail. Blocking them keeps that private.",
  "privacy.block": "Ask before showing images",
  "privacy.blockHelp": "Images and other remote content stay hidden until you choose to show them for a message.",
  "privacy.always": "Always show images",
  "privacy.alwaysHelp": "Images load automatically. Senders may learn when you open their mail.",

  "dev.tokens": "API tokens",
  "dev.tokensHelp": "Let scripts and other tools use your mailbox through the API, authenticated with",
  "dev.apiDocs": "API reference",
  "dev.tokenCreated": "Token created. Copy it now: it won't be shown again.",
  "dev.label": "Name",
  "dev.tokenLabelPlaceholder": "e.g. Backup script",
  "dev.appLabelPlaceholder": "e.g. Phone, Thunderbird",
  "dev.scope": "Access",
  "dev.scopeRead": "Read: list and search",
  "dev.scopeSend": "Send: read and write",
  "dev.scopeFull": "Full: every action",
  "dev.expires": "Expires (days)",
  "dev.never": "Never",
  "dev.creating": "Creating…",
  "dev.createToken": "Create token",
  "dev.noTokens": "No tokens yet.",
  "dev.created": "Created {date}",
  "dev.lastUsed": "last used {date}",
  "dev.expiresOn": "expires {date}",
  "dev.revoke": "Revoke",
  "dev.revoked": "revoked",
  "dev.appPasswords": "App passwords",
  "dev.appPasswordsHelp": "Separate passwords for mail apps, each revocable on its own.",
  "dev.appPasswordsPreview": "App passwords aren't accepted at sign-in yet. This is a preview.",
  "dev.appPasswordCreated": "App password created. Copy it now: it won't be shown again.",
  "dev.createAppPassword": "Create app password",
  "dev.noAppPasswords": "No app passwords yet.",

  "admin.users": "Users",
  "admin.usersHelp": { one: "1 mailbox on this server.", other: "{n} mailboxes on this server." },
  "admin.noUsers": "No users found.",
  "admin.email": "Email",
  "admin.localpart": "Local part",
  "admin.allTokens": "All API tokens",
  "admin.audit": "Audit log",
  "admin.auditTotal": "{n} entries in total, newest first.",
  "admin.auditFilter": "Filter by user, action or IP",
  "admin.noAudit": "No entries.",
  "admin.time": "Time",
  "admin.actor": "User",
  "admin.action": "Action",
  "admin.detail": "Detail",
  "admin.softDeletes": "Deleted messages",
  "admin.noSoftDeletes": "No deleted messages recorded.",
  "admin.mailbox": "Folder",
  "admin.deletedAt": "Deleted",
  "admin.expunged": "Erased",
  "admin.retention": "Retention policies",
  "admin.retentionHelp": "Automatically remove messages older than a number of days from a folder.",
  "admin.mailboxPath": "Folder path (e.g. INBOX)",
  "admin.maxAge": "Max age (days)",
  "admin.addPolicy": "Add or update",
  "admin.noPolicies": "No retention policies.",
  "admin.created": "Created",

  "login.title": "Sign in to your mail",
  "login.subtitle": "Use your full email address and mailbox password.",
  "login.email": "Email",
  "login.emailPlaceholder": "you@example.com",
  "login.password": "Password",
  "login.show": "Show password",
  "login.hide": "Hide password",
  "login.capsLock": "Caps Lock is on",
  "login.submit": "Sign in",
  "login.signingIn": "Signing in…",
  "login.privacy": "Your password only opens a session with your mail server. It's never stored.",
} satisfies Record<string, Msg>;

export type MsgKey = keyof typeof en;

const es: Record<MsgKey, Msg> = {
  "common.cancel": "Cancelar",
  "common.close": "Cerrar",
  "common.copy": "Copiar",
  "common.deleteForever": "Eliminar definitivamente",
  "common.dismiss": "Ocultar",
  "common.emptyNow": "Vaciar ahora",
  "common.loading": "Cargando",
  "common.more": "Más",
  "common.no": "no",
  "common.yes": "sí",
  "common.noSubject": "(sin asunto)",
  "common.remove": "Quitar",
  "common.rename": "Cambiar nombre",
  "common.retry": "Reintentar",
  "common.saveChanges": "Guardar cambios",
  "common.saving": "Guardando…",
  "common.undo": "Deshacer",

  "folder.inbox": "Recibidos",
  "folder.drafts": "Borradores",
  "folder.sent": "Enviados",
  "folder.archive": "Archivo",
  "folder.junk": "Spam",
  "folder.trash": "Papelera",
  "folder.flagged": "Destacados",
  "folder.all": "Todo el correo",

  "date.today": "Hoy",
  "date.yesterday": "Ayer",
  "date.week": "Esta semana",
  "date.month": "Este mes",

  "nav.label": "Carpetas y cuenta",
  "nav.collapse": "Contraer barra lateral",
  "nav.expand": "Expandir barra lateral",
  "nav.open": "Abrir carpetas",
  "nav.folders": "Carpetas",
  "nav.newFolder": "Nueva carpeta",
  "nav.newSubfolder": "Nueva subcarpeta",
  "nav.folderName": "Nombre de la carpeta",
  "nav.subfolderOf": "Nueva carpeta dentro de {name}",
  "nav.noFolders": "Aún no hay carpetas",
  "nav.folderOptions": "Opciones de carpeta",
  "nav.markAllRead": "Marcar todo como leído",
  "nav.emptyFolder": "Vaciar carpeta",
  "nav.deleteFolder": "Eliminar carpeta",
  "nav.collapseFolder": "Ocultar subcarpetas",
  "nav.expandFolder": "Mostrar subcarpetas",
  "nav.signOut": "Cerrar sesión",

  "list.label": "Mensajes",
  "list.summary": { one: "1 mensaje", other: "{n} mensajes" },
  "list.summaryUnread": "{n} mensajes, {u} sin leer",
  "list.summaryFilterUnread": { one: "1 mensaje sin leer", other: "{n} mensajes sin leer" },
  "list.summaryFilterStarred": { one: "1 mensaje destacado", other: "{n} mensajes destacados" },
  "list.refresh": "Buscar correo nuevo",
  "list.folderActions": "Acciones de carpeta",
  "list.filter": "Mostrar",
  "list.filter.all": "Todos",
  "list.filter.unread": "Sin leer",
  "list.filter.starred": "Destacados",
  "list.selectAll": "Seleccionar todos",
  "list.clearSelection": "Quitar selección",
  "list.select": "Seleccionar mensaje",
  "list.deselect": "Deseleccionar mensaje",
  "list.selected": { one: "1 seleccionado", other: "{n} seleccionados" },
  "list.loadError": "No se pudieron cargar los mensajes",
  "list.loadMore": "Cargar más",
  "list.toPrefix": "Para:",
  "list.noRecipients": "(sin destinatarios)",
  "list.draftTag": "Borrador",
  "list.unread": "sin leer",

  "search.label": "Buscar correo",
  "search.placeholder": "Buscar en {folder}",
  "search.options": "Opciones de búsqueda",
  "search.clear": "Borrar búsqueda",
  "search.results": "Resultados",
  "search.searching": "Buscando…",
  "search.count": { one: "1 resultado", other: "{n} resultados" },
  "search.lookIn": "Buscar en",
  "search.field.all": "Todo",
  "search.field.from": "De",
  "search.field.to": "Para",
  "search.field.subject": "Asunto",
  "search.field.body": "Texto",
  "search.since": "Desde",
  "search.before": "Hasta",
  "search.onlyUnread": "Solo sin leer",
  "search.onlyStarred": "Solo destacados",
  "search.allFolders": "Buscar en todas las carpetas",
  "search.reset": "Restablecer",
  "search.apply": "Buscar",
  "search.noneTitle": "Sin coincidencias",
  "search.noneBody": "Prueba con otras palabras o busca en todas las carpetas desde las opciones.",

  "empty.inboxTitle": "Estás al día",
  "empty.inboxBody": "El correo nuevo aparecerá aquí.",
  "empty.draftsTitle": "No hay borradores",
  "empty.draftsBody": "Lo que empiezas a escribir y no envías se guarda aquí automáticamente.",
  "empty.sentTitle": "Aún no has enviado nada",
  "empty.sentBody": "Los mensajes que envíes aparecerán aquí.",
  "empty.trashTitle": "La papelera está vacía",
  "empty.trashBody": "Los mensajes eliminados quedan aquí hasta que la vacíes.",
  "empty.spamTitle": "No hay spam",
  "empty.spamBody": "Los mensajes marcados como spam terminan aquí.",
  "empty.folderTitle": "Esta carpeta está vacía",
  "empty.folderBody": "Arrastra mensajes sobre ella en la barra lateral para guardarlos aquí.",
  "empty.unreadTitle": "No hay mensajes sin leer",
  "empty.unreadBody": "Ya leíste todo lo de esta carpeta.",
  "empty.starredTitle": "No hay mensajes destacados",
  "empty.starredBody": "Destaca un mensaje para encontrarlo rápido aquí.",
  "empty.showAll": "Ver todos los mensajes",

  "action.archive": "Archivar",
  "action.delete": "Eliminar",
  "action.spam": "Marcar como spam",
  "action.notSpam": "No es spam",
  "action.move": "Mover a…",
  "action.markRead": "Marcar como leído",
  "action.markUnread": "Marcar como no leído",
  "action.star": "Destacar",
  "action.unstar": "Quitar destacado",
  "action.reply": "Responder",
  "action.replyAll": "Responder a todos",
  "action.forward": "Reenviar",

  "reader.label": "Mensaje",
  "reader.back": "Volver a la lista",
  "reader.position": "{i} de {n}",
  "reader.newer": "Mensaje más reciente",
  "reader.older": "Mensaje más antiguo",
  "reader.print": "Imprimir",
  "reader.source": "Ver original",
  "reader.downloadEml": "Descargar como .eml",
  "reader.copySender": "Copiar dirección del remitente",
  "reader.loadError": "No se pudo abrir este mensaje",
  "reader.remoteBlocked": "Las imágenes del remitente están ocultas para que no sepa cuándo lees esto.",
  "reader.showImages": "Mostrar imágenes",
  "reader.alwaysShow": "Mostrar siempre",
  "reader.mailingList": "Este mensaje viene de una lista de correo.",
  "reader.unsubscribe": "Darse de baja",
  "reader.toLabel": "Para",
  "reader.me": "mí",
  "reader.undisclosed": "destinatarios ocultos",
  "reader.andMore": "{list} y {n} más",
  "reader.replyTo": "Responder a",
  "reader.thread": { one: "1 mensaje en esta conversación", other: "{n} mensajes en esta conversación" },
  "reader.empty": "Este mensaje no tiene texto.",
  "reader.body": "Cuerpo del mensaje",
  "reader.attachments": "Adjuntos",
  "reader.attachmentsCount": { one: "1 adjunto", other: "{n} adjuntos" },
  "reader.view": "Vista previa",
  "reader.download": "Descargar",
  "reader.openTab": "Abrir en otra pestaña",
  "reader.emptyTitle": "Elige un mensaje para leerlo",
  "reader.emptyBody": "Se abre aquí, junto a la lista.",
  "reader.hintNavigate": "moverse",
  "reader.hintCompose": "escribir",
  "reader.hintHelp": "todos los atajos",

  "eml.title": "Mensaje adjunto",
  "eml.sub": "Vista de solo lectura de un mensaje adjunto a este correo",
  "eml.loading": "Abriendo el mensaje adjunto…",
  "eml.error": "No se pudo previsualizar este adjunto.",
  "eml.nested": { one: "1 adjunto dentro", other: "{n} adjuntos dentro" },

  "compose.new": "Nuevo mensaje",
  "compose.reply": "Responder",
  "compose.replyAll": "Responder a todos",
  "compose.forward": "Reenviar",
  "compose.draft": "Borrador",
  "compose.from": "De",
  "compose.to": "Para",
  "compose.date": "Fecha",
  "compose.subject": "Asunto",
  "compose.subjectPlaceholder": "Asunto",
  "compose.body": "Mensaje",
  "compose.bodyPlaceholder": "Escribe tu mensaje…",
  "compose.recipientPlaceholder": "Nombre o correo",
  "compose.invalidAddress": "Esto no parece una dirección de correo",
  "compose.removeRecipient": "Quitar a {name}",
  "compose.removeAttachment": "Quitar {name}",
  "compose.send": "Enviar",
  "compose.sending": "Enviando…",
  "compose.sendAnyway": "Enviar igual",
  "compose.attach": "Adjuntar archivos",
  "compose.dropFiles": "Suelta los archivos para adjuntarlos",
  "compose.discard": "Descartar",
  "compose.saveClose": "Guardar borrador",
  "compose.saving": "Guardando…",
  "compose.saved": "Borrador guardado",
  "compose.minimize": "Minimizar",
  "compose.restore": "Restaurar",
  "compose.maximize": "Pantalla completa",
  "compose.dock": "Salir de pantalla completa",
  "compose.unsavedTitle": "¿Guardar este borrador?",
  "compose.unsavedBody": "Tienes cambios que aún no se han guardado.",
  "compose.linkPrompt": "Dirección del enlace (https:// o mailto:)",
  "compose.needRecipient": "Agrega al menos un destinatario.",
  "compose.badRecipient": "Revisa esta dirección: {addr}",
  "compose.stillAttaching": "Espera a que terminen de cargar los adjuntos.",
  "compose.tooLarge": "{name} es demasiado grande: los adjuntos pueden sumar como máximo 25 MB.",
  "compose.readFailed": "No se pudo leer {name}.",
  "compose.quoteHeader": "El {date}, {who} escribió:",
  "compose.fwdHeader": "---------- Mensaje reenviado ----------",

  "format.bold": "Negrita",
  "format.italic": "Cursiva",
  "format.underline": "Subrayado",
  "format.bullets": "Lista con viñetas",
  "format.numbers": "Lista numerada",
  "format.quote": "Cita",
  "format.link": "Insertar enlace",
  "format.clear": "Quitar formato",

  "confirm.deleteForever.title": { one: "¿Eliminar este mensaje definitivamente?", other: "¿Eliminar {n} mensajes definitivamente?" },
  "confirm.deleteForever.body": "Ya están en la papelera. No se puede deshacer.",
  "confirm.empty.title": "¿Vaciar {folder}?",
  "confirm.empty.body": "Todos sus mensajes se eliminarán para siempre. No se puede deshacer.",
  "confirm.noSubject.title": "¿Enviar sin asunto?",
  "confirm.noSubject.body": "El asunto ayuda a quien lo recibe a saber de qué trata tu mensaje.",
  "confirm.deleteFolder.title": "¿Eliminar «{name}»?",
  "confirm.deleteFolder.kids": "Puede que también se eliminen sus subcarpetas.",
  "confirm.deleteFolder.notEmpty": { one: "Contiene 1 mensaje.", other: "Contiene {n} mensajes." },

  "toast.archived": { one: "Archivado", other: "{n} archivados" },
  "toast.trashed": { one: "Movido a la papelera", other: "{n} movidos a la papelera" },
  "toast.deleted": { one: "Eliminado definitivamente", other: "{n} eliminados definitivamente" },
  "toast.spammed": { one: "Marcado como spam", other: "{n} marcados como spam" },
  "toast.moved": { one: "Movido a {folder}", other: "{n} movidos a {folder}" },
  "toast.undone": "Deshecho",
  "toast.actionFailed": "No funcionó. Revisa tu conexión e inténtalo de nuevo.",
  "toast.noArchive": "Esta cuenta no tiene carpeta de archivo.",
  "toast.noSpam": "Esta cuenta no tiene carpeta de spam.",
  "toast.sending": "Enviando…",
  "toast.sent": "Mensaje enviado",
  "toast.sentNoCopy": "Mensaje enviado, pero no se guardó una copia en Enviados",
  "toast.draftDiscarded": "Borrador descartado",
  "toast.folderCreated": "Carpeta «{name}» creada",
  "toast.markedAllRead": "Todo en {folder} marcado como leído",
  "toast.emptied": { one: "1 mensaje eliminado", other: "{n} mensajes eliminados" },
  "toast.copied": "Copiado",
  "toast.copyFailed": "No se pudo copiar al portapapeles",

  "notify.one": "Mensaje nuevo",
  "notify.many": "{n} mensajes nuevos",

  "move.title": "Mover a carpeta",
  "move.placeholder": { one: "Mover 1 mensaje a…", other: "Mover {n} mensajes a…" },
  "move.create": "Crear la carpeta «{name}» y mover",
  "move.none": "Ninguna carpeta coincide",

  "palette.title": "Paleta de comandos",
  "palette.placeholder": "Escribe un comando, carpeta o búsqueda…",
  "palette.go": "Ir a",
  "palette.actions": "Acciones",
  "palette.view": "Vista",
  "palette.search": "Buscar",
  "palette.goTo": "Ir a {folder}",
  "palette.markFolderRead": "Marcar todo en {folder} como leído",
  "palette.lightTheme": "Cambiar a tema claro",
  "palette.darkTheme": "Cambiar a tema oscuro",
  "palette.compact": "Usar filas compactas",
  "palette.comfortable": "Usar filas amplias",
  "palette.paneOff": "Ocultar el panel de lectura",
  "palette.paneRight": "Mostrar el panel de lectura",
  "palette.searchHere": "Buscar «{q}» en {folder}",
  "palette.searchAll": "Buscar «{q}» en todas las carpetas",

  "shortcuts.title": "Atajos de teclado",
  "shortcuts.navigation": "Navegación",
  "shortcuts.actions": "Mensajes",
  "shortcuts.writing": "Escritura",
  "shortcuts.next": "Mensaje más antiguo",
  "shortcuts.prev": "Mensaje más reciente",
  "shortcuts.goInbox": "Ir a Recibidos",
  "shortcuts.goSent": "Ir a Enviados",
  "shortcuts.goDrafts": "Ir a Borradores",
  "shortcuts.goArchive": "Ir a Archivo",
  "shortcuts.search": "Buscar",
  "shortcuts.palette": "Paleta de comandos",
  "shortcuts.escape": "Cerrar o volver",
  "shortcuts.toggleRead": "Marcar leído / no leído",
  "shortcuts.toggleStar": "Destacar / quitar",
  "shortcuts.select": "Seleccionar mensaje",
  "shortcuts.undo": "Deshacer la última acción",
  "shortcuts.saveDraft": "Guardar borrador",

  "layout.resize": "Cambiar el ancho de la lista",

  "theme.light": "Claro",
  "theme.dark": "Oscuro",
  "theme.auto": "Auto",

  "settings.title": "Ajustes",
  "settings.general": "General",
  "settings.account": "Cuenta",
  "settings.privacy": "Privacidad",
  "settings.developer": "Desarrolladores",
  "settings.admin": "Administración",
  "settings.appearance": "Apariencia",
  "settings.language": "Idioma",
  "settings.theme": "Tema",
  "settings.density": "Lista de mensajes",
  "settings.densityHelp": "La vista compacta muestra más mensajes en pantalla.",
  "settings.comfortable": "Amplia",
  "settings.compact": "Compacta",
  "settings.readingPane": "Panel de lectura",
  "settings.readingPaneHelp": "Lee junto a la lista o abre los mensajes a todo el ancho.",
  "settings.paneRight": "A la derecha",
  "settings.paneOff": "Desactivado",
  "settings.previews": "Vista previa de mensajes",
  "settings.previewsHelp": "Muestra la primera línea de cada mensaje en la lista.",
  "settings.behaviour": "Comportamiento",
  "settings.afterMove": "Después de archivar o eliminar",
  "settings.afterMoveHelp": "Qué mostrar cuando el mensaje abierto desaparece.",
  "settings.afterNext": "Siguiente mensaje",
  "settings.afterList": "La lista",
  "settings.undoSend": "Deshacer envío",
  "settings.undoSendHelp": "Cuánto tiempo tienes para recuperar un mensaje después de pulsar Enviar.",
  "settings.undoOff": "Desactivado",
  "settings.seconds": "{n} segundos",
  "settings.notifications": "Notificaciones de escritorio",
  "settings.notificationsHelp": "Avisa del correo nuevo mientras esta pestaña está en segundo plano.",
  "settings.notificationsDenied": "Tu navegador bloquea las notificaciones de este sitio.",
  "settings.identity": "Nombre y firma",
  "settings.identityHelp": "Lo que ven las personas a quienes escribes desde {email}.",
  "settings.displayName": "Tu nombre",
  "settings.fromPreview": "Los destinatarios verán",
  "settings.signature": "Firma",
  "settings.signaturePlaceholder": "Se añade al final de los mensajes nuevos",
  "settings.signatureOnReplies": "Añadir la firma en respuestas y reenvíos",
  "settings.signatureOnRepliesHelp": "Se coloca encima del mensaje citado.",
  "settings.saved": "Ajustes guardados",

  "password.title": "Contraseña",
  "password.help": "Cambia la contraseña de tu buzón. Debe tener entre {min} y {max} caracteres.",
  "password.unavailable": "Aún no se puede cambiar la contraseña desde aquí. Puedes hacerlo en la página de cuenta del servidor de correo.",
  "password.openAccountPage": "Abrir página de cuenta",
  "password.current": "Contraseña actual",
  "password.new": "Contraseña nueva",
  "password.confirm": "Confirma la contraseña nueva",
  "password.change": "Cambiar contraseña",
  "password.changing": "Cambiando…",
  "password.changed": "Contraseña cambiada. Sigues con la sesión iniciada en este dispositivo.",
  "password.fillAll": "Completa los tres campos.",
  "password.length": "La contraseña nueva debe tener entre {min} y {max} caracteres.",
  "password.mismatch": "La contraseña nueva y su confirmación no coinciden.",
  "password.same": "La contraseña nueva debe ser distinta de la actual.",
  "password.failed": "No se pudo cambiar la contraseña.",

  "privacy.title": "Imágenes remotas",
  "privacy.help": "Los remitentes pueden usar imágenes remotas para saber cuándo y dónde abres sus correos. Bloquearlas lo mantiene en privado.",
  "privacy.block": "Preguntar antes de mostrar imágenes",
  "privacy.blockHelp": "Las imágenes y el contenido remoto quedan ocultos hasta que decides mostrarlos en un mensaje.",
  "privacy.always": "Mostrar siempre las imágenes",
  "privacy.alwaysHelp": "Las imágenes se cargan solas. Los remitentes podrían saber cuándo abres sus correos.",

  "dev.tokens": "Tokens de API",
  "dev.tokensHelp": "Permiten que scripts y otras herramientas usen tu buzón a través de la API, autenticándose con",
  "dev.apiDocs": "Referencia de la API",
  "dev.tokenCreated": "Token creado. Cópialo ahora: no se volverá a mostrar.",
  "dev.label": "Nombre",
  "dev.tokenLabelPlaceholder": "p. ej. Script de respaldo",
  "dev.appLabelPlaceholder": "p. ej. Teléfono, Thunderbird",
  "dev.scope": "Acceso",
  "dev.scopeRead": "Lectura: listar y buscar",
  "dev.scopeSend": "Envío: leer y escribir",
  "dev.scopeFull": "Completo: todas las acciones",
  "dev.expires": "Caduca (días)",
  "dev.never": "Nunca",
  "dev.creating": "Creando…",
  "dev.createToken": "Crear token",
  "dev.noTokens": "Aún no hay tokens.",
  "dev.created": "Creado el {date}",
  "dev.lastUsed": "usado por última vez el {date}",
  "dev.expiresOn": "caduca el {date}",
  "dev.revoke": "Revocar",
  "dev.revoked": "revocado",
  "dev.appPasswords": "Contraseñas de aplicación",
  "dev.appPasswordsHelp": "Contraseñas separadas para apps de correo, cada una revocable por su cuenta.",
  "dev.appPasswordsPreview": "Las contraseñas de aplicación aún no se aceptan al iniciar sesión. Es una vista previa.",
  "dev.appPasswordCreated": "Contraseña de aplicación creada. Cópiala ahora: no se volverá a mostrar.",
  "dev.createAppPassword": "Crear contraseña de aplicación",
  "dev.noAppPasswords": "Aún no hay contraseñas de aplicación.",

  "admin.users": "Usuarios",
  "admin.usersHelp": { one: "1 buzón en este servidor.", other: "{n} buzones en este servidor." },
  "admin.noUsers": "No se encontraron usuarios.",
  "admin.email": "Correo",
  "admin.localpart": "Parte local",
  "admin.allTokens": "Todos los tokens de API",
  "admin.audit": "Registro de auditoría",
  "admin.auditTotal": "{n} entradas en total, las más recientes primero.",
  "admin.auditFilter": "Filtrar por usuario, acción o IP",
  "admin.noAudit": "No hay entradas.",
  "admin.time": "Hora",
  "admin.actor": "Usuario",
  "admin.action": "Acción",
  "admin.detail": "Detalle",
  "admin.softDeletes": "Mensajes eliminados",
  "admin.noSoftDeletes": "No hay mensajes eliminados registrados.",
  "admin.mailbox": "Carpeta",
  "admin.deletedAt": "Eliminado",
  "admin.expunged": "Borrado",
  "admin.retention": "Políticas de retención",
  "admin.retentionHelp": "Elimina automáticamente de una carpeta los mensajes con más de cierta cantidad de días.",
  "admin.mailboxPath": "Ruta de carpeta (p. ej. INBOX)",
  "admin.maxAge": "Antigüedad máx. (días)",
  "admin.addPolicy": "Añadir o actualizar",
  "admin.noPolicies": "No hay políticas de retención.",
  "admin.created": "Creada",

  "login.title": "Entra a tu correo",
  "login.subtitle": "Usa tu dirección de correo completa y la contraseña de tu buzón.",
  "login.email": "Correo",
  "login.emailPlaceholder": "tu@ejemplo.com",
  "login.password": "Contraseña",
  "login.show": "Mostrar contraseña",
  "login.hide": "Ocultar contraseña",
  "login.capsLock": "Bloq Mayús está activado",
  "login.submit": "Iniciar sesión",
  "login.signingIn": "Iniciando sesión…",
  "login.privacy": "Tu contraseña solo abre una sesión con tu servidor de correo. Nunca se guarda.",
};

const DICTS: Record<string, Record<MsgKey, Msg>> = { en, es };

/** Active locale, as a BCP-47 tag usable with Intl / toLocale*String. */
export const locale = computed(() => (prefs.value.locale === "es" ? "es-CL" : "en"));

export function t(key: MsgKey, vars: Record<string, string | number> = {}): string {
  const dict = DICTS[prefs.value.locale] ?? en;
  let msg: Msg = dict[key] ?? en[key] ?? key;
  if (typeof msg !== "string") msg = vars.n === 1 ? msg.one : msg.other;
  return msg.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}
